/**
 * Role kolumn w mobilnym trybie „Lista” (wariant C makiety 2026-10-04) i
 * zapamietywanie trybu Lista/Tabela per tabela. Czysta logika bez Reacta —
 * wspolna dla DataTable i AdminTable; testy w `__tests__/table-mobile.test.ts`.
 *
 * Domyslne role (bez zadnej konfiguracji):
 *  - tytul: pierwsza kolumna tekstowa (nie liczbowa, nie data),
 *  - podtytul: druga kolumna tekstowa, a gdy jej brak — pierwsza kolumna daty,
 *  - miary: kolumny liczbowe; domyslna miara to pierwsza addytywna, potem pierwsza,
 *  - akcje: kolumna `actions` / `meta.alwaysLast` (stopka ekranu szczegolow).
 * Jawne `meta.mobileRole` wygrywa. Gdy ktorakolwiek kolumna ma
 * `mobileRole: "measure"`, miarami sa wylacznie kolumny oznaczone jawnie.
 */

export type MobileColumnRole = "title" | "subtitle" | "measure" | "detail" | "actions";
export type TableMobileMode = "list" | "table";

export interface RoleCandidate {
	id: string;
	/** Jawna rola z `meta.mobileRole`. */
	role?: MobileColumnRole;
	numeric: boolean;
	date: boolean;
	/** Kolumna akcji wiersza (`actions` / `alwaysLast`). */
	actions: boolean;
	/** Miara addytywna (sumowalna) — tylko ona trafia do paska sumy. */
	additive: boolean;
	/** `meta.mobileDefaultMeasure`. */
	defaultMeasure?: boolean;
}

export interface ResolvedColumnRoles {
	titleId: string | null;
	subtitleIds: string[];
	measureIds: string[];
	defaultMeasureId: string | null;
	actionIds: string[];
	/** Wszystkie kolumny poza akcjami, w kolejnosci widocznej — ekran szczegolow. */
	detailIds: string[];
}

export function resolveColumnRoles(candidates: RoleCandidate[]): ResolvedColumnRoles {
	const isActions = (c: RoleCandidate) =>
		c.role === "actions" || (c.role === undefined && c.actions);
	const actionIds = candidates.filter(isActions).map((c) => c.id);
	const content = candidates.filter((c) => !isActions(c));
	const unassigned = (c: RoleCandidate) => c.role === undefined;
	const isText = (c: RoleCandidate) => !c.numeric && !c.date;

	const titleId =
		content.find((c) => c.role === "title")?.id ??
		content.find((c) => unassigned(c) && isText(c))?.id ??
		content.find((c) => unassigned(c) && !c.numeric)?.id ??
		content.find(unassigned)?.id ??
		null;

	const explicitSubtitles = content.filter((c) => c.role === "subtitle").map((c) => c.id);
	let subtitleIds = explicitSubtitles;
	if (explicitSubtitles.length === 0) {
		const free = content.filter((c) => unassigned(c) && c.id !== titleId);
		const fallback = free.find(isText) ?? free.find((c) => c.date && !c.numeric);
		subtitleIds = fallback ? [fallback.id] : [];
	}

	const explicitMeasures = content.filter((c) => c.role === "measure");
	const measures =
		explicitMeasures.length > 0
			? explicitMeasures
			: content.filter(
					(c) => unassigned(c) && c.numeric && c.id !== titleId && !subtitleIds.includes(c.id),
				);
	const measureIds = measures.map((c) => c.id);
	const defaultMeasureId =
		measures.find((c) => c.defaultMeasure)?.id ??
		measures.find((c) => c.additive)?.id ??
		measures[0]?.id ??
		null;

	return {
		titleId,
		subtitleIds,
		measureIds,
		defaultMeasureId,
		actionIds,
		detailIds: content.map((c) => c.id),
	};
}

/* ============================================================================
   Zapamietywanie trybu per tabela
   ============================================================================ */

export const DEFAULT_TABLE_MOBILE_MODE: TableMobileMode = "list";

export function tableModeStorageKey(tableId: string): string {
	return `farmaster_dt_${tableId}_mobileMode`;
}

export function readTableMode(
	tableId: string,
	fallback: TableMobileMode = DEFAULT_TABLE_MOBILE_MODE,
): TableMobileMode {
	if (typeof window === "undefined") return fallback;
	try {
		const raw = localStorage.getItem(tableModeStorageKey(tableId));
		return raw === "table" || raw === "list" ? raw : fallback;
	} catch {
		return fallback;
	}
}

export function writeTableMode(tableId: string, mode: TableMobileMode): void {
	if (typeof window === "undefined") return;
	try {
		localStorage.setItem(tableModeStorageKey(tableId), mode);
	} catch {
		return;
	}
}

/**
 * Stabilny klucz dla tabel bez `tableId`: sciezka strony + pierwsze kolumny.
 * Dwie rozne tabele na jednej stronie maja rozne kolumny, wiec sie nie zderzaja.
 */
export function fallbackTableId(prefix: string, columnIds: string[]): string {
	const path = typeof window === "undefined" ? "" : window.location.pathname;
	return `${prefix}:${path}:${columnIds.slice(0, 4).join(",")}`;
}
